import React, { useState, useCallback } from "react";
import { Combobox, ComboboxInput, ComboboxButton, ComboboxOptions, ComboboxOption } from "@headlessui/react";
import { ChevronUpDownIcon, CheckIcon, PlusIcon } from "@heroicons/react/24/outline";

/**
 * Searchable combobox with inline "create new" support.
 *
 * @param {Object}   props
 * @param {Array}    props.options       - Array of { value, label, pk? } objects
 * @param {string}   props.value         - Currently selected value (string)
 * @param {function} props.onChange       - (value: string) => void
 * @param {function} props.onCreateNew   - (query: string) => Promise<{ value, label }>  — inline create
 * @param {string}   props.placeholder   - Placeholder text
 * @param {string}   props.label         - Optional label
 * @param {boolean}  props.required      - Show red asterisk
 * @param {boolean}  props.disabled      - Whether the input is disabled
 * @param {boolean}  props.loading       - External loading state
 * @param {string}   props.className     - Additional classes for the wrapper
 */
export default function CreatableCombobox({
  options = [],
  value,
  onChange,
  onCreateNew,
  placeholder = "Search...",
  label,
  required = false,
  disabled = false,
  loading = false,
  className = "",
}) {
  const [query, setQuery] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [creating, setCreating] = useState(false);

  // Always show the current value even if it's not in options (e.g., catalog
  // hasn't loaded yet, or the value was entered as free-text before catalogs existed)
  const matchedOption = options.find((opt) => opt.value === value);
  const selectedOption = matchedOption || (value ? { value, label: value } : null);

  // If the value exists but isn't in options, inject it so it appears in the list
  const effectiveOptions =
    value && !matchedOption
      ? [{ value, label: value }, ...options]
      : options;

  const filteredOptions =
    query === "" || !isTyping
      ? effectiveOptions
      : effectiveOptions.filter((opt) =>
          opt.label.toLowerCase().includes(query.toLowerCase())
        );

  // Show "Add new" when typing a query that has no exact match
  const queryTrimmed = query.trim();
  const hasExactMatch =
    queryTrimmed !== "" &&
    effectiveOptions.some((opt) => opt.label.toLowerCase() === queryTrimmed.toLowerCase());
  const showCreateOption = isTyping && queryTrimmed !== "" && !hasExactMatch && onCreateNew;

  const handleChange = (option) => {
    if (!option) return;
    if (option.__isNew) {
      // User selected the "Add new" option — trigger creation
      handleCreateNew();
      return;
    }
    onChange(option.value);
    setQuery("");
    setIsTyping(false);
  };

  const handleInputChange = (event) => {
    setQuery(event.target.value);
    setIsTyping(true);
  };

  const handleFocus = (event) => {
    event.target.select();
    setIsTyping(false);
  };

  const handleCreateNew = useCallback(async () => {
    if (!onCreateNew || !queryTrimmed || creating) return;
    setCreating(true);
    try {
      const newOption = await onCreateNew(queryTrimmed);
      if (newOption) {
        onChange(newOption.value);
      }
      setQuery("");
      setIsTyping(false);
    } catch (e) {
      console.error("Failed to create new entry:", e);
    } finally {
      setCreating(false);
    }
  }, [onCreateNew, queryTrimmed, creating, onChange]);

  return (
    <div className={className}>
      {label && (
        <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300 tw-mb-1">
          {label} {required && <span className="tw-text-red-600">*</span>}
        </label>
      )}
      <Combobox value={selectedOption} onChange={handleChange} disabled={disabled}>
        <div className="tw-relative">
          <ComboboxButton className="tw-w-full">
            <ComboboxInput
              autoComplete="off"
              className={`tw-w-full tw-rounded-md tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-bg-white dark:tw-bg-gray-800 tw-px-3 tw-py-2 tw-pr-8 tw-text-sm tw-text-gray-900 dark:tw-text-white tw-placeholder-gray-400 focus:tw-border-blue-500 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500 ${
                disabled ? "tw-bg-gray-100 dark:tw-bg-gray-700 tw-cursor-not-allowed" : ""
              }`}
              displayValue={(option) => option?.label || ""}
              onChange={handleInputChange}
              onFocus={handleFocus}
              placeholder={placeholder}
            />
            <span className="tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-items-center tw-pr-2 tw-pointer-events-none">
              {(loading || creating) ? (
                <svg className="tw-animate-spin tw-h-4 tw-w-4 tw-text-gray-400" viewBox="0 0 24 24" fill="none">
                  <circle className="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : (
                <ChevronUpDownIcon className="tw-h-4 tw-w-4 tw-text-gray-400" aria-hidden="true" />
              )}
            </span>
          </ComboboxButton>

          <ComboboxOptions className="tw-absolute tw-z-50 tw-mt-1 tw-max-h-60 tw-w-full tw-overflow-auto tw-rounded-md tw-bg-white dark:tw-bg-gray-800 tw-py-1 tw-text-sm tw-shadow-lg tw-ring-1 tw-ring-black/5 dark:tw-ring-white/10 focus:tw-outline-none">
            {filteredOptions.length === 0 && !showCreateOption ? (
              <div className="tw-relative tw-cursor-default tw-select-none tw-px-3 tw-py-2 tw-text-gray-500 dark:tw-text-gray-400">
                {queryTrimmed ? "No results found" : "No options available"}
              </div>
            ) : (
              filteredOptions.map((option) => (
                <ComboboxOption
                  key={option.value}
                  value={option}
                  className="tw-relative tw-cursor-pointer tw-select-none tw-py-2 tw-pl-8 tw-pr-3 tw-text-gray-900 dark:tw-text-white data-[focus]:tw-bg-blue-50 dark:data-[focus]:tw-bg-blue-900/50"
                >
                  {({ selected }) => (
                    <>
                      <span className={`tw-block tw-truncate ${selected ? "tw-font-medium" : "tw-font-normal"}`}>
                        {option.label}
                      </span>
                      {selected && (
                        <span className="tw-absolute tw-inset-y-0 tw-left-0 tw-flex tw-items-center tw-pl-2 tw-text-blue-600 dark:tw-text-blue-400">
                          <CheckIcon className="tw-h-4 tw-w-4" aria-hidden="true" />
                        </span>
                      )}
                    </>
                  )}
                </ComboboxOption>
              ))
            )}

            {showCreateOption && (
              <ComboboxOption
                value={{ value: queryTrimmed, label: queryTrimmed, __isNew: true }}
                className="tw-relative tw-cursor-pointer tw-select-none tw-py-2 tw-pl-8 tw-pr-3 tw-text-blue-600 dark:tw-text-blue-400 tw-border-t tw-border-gray-100 dark:tw-border-gray-700 data-[focus]:tw-bg-blue-50 dark:data-[focus]:tw-bg-blue-900/50"
              >
                <span className="tw-absolute tw-inset-y-0 tw-left-0 tw-flex tw-items-center tw-pl-2">
                  <PlusIcon className="tw-h-4 tw-w-4" aria-hidden="true" />
                </span>
                <span className="tw-block tw-truncate tw-font-medium">
                  Add "{queryTrimmed}"
                </span>
              </ComboboxOption>
            )}
          </ComboboxOptions>
        </div>
      </Combobox>
    </div>
  );
}
